import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Container,
  Paper,
  IconButton,
  Stack,
  Divider,
  Skeleton,
} from "@mui/material";
import {
  Favorite as WishlistIcon,
  ArrowForwardIosRounded as ArrowForwardIosRoundedIcon,
  FavoriteBorder as FavoriteBorderIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import config from "../../Config";
import ProfileSideBar from "./ProfileSideBar";

const COLORS = {
  cream: "#F7F4EF",
  softCream: "#FBF9F6",
  white: "#FFFFFF",
  black: "#171717",
  mutedBlack: "#3A3A3A",
  grey: "#77736D",
  lightGrey: "#A29D95",
  border: "#DDD7CE",
  gold: "#977743",
  goldLight: "#C4A36A",
};

const WishlistSkeleton = () => (
  <Grid item xs={12} sm={6} lg={4}>
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${COLORS.border}`,
        borderRadius: 0,
        overflow: "hidden",
        backgroundColor: COLORS.white,
      }}
    >
      <Skeleton
        variant="rectangular"
        sx={{
          width: "100%",
          height: {
            xs: 320,
            sm: 300,
          },
        }}
      />

      <Box sx={{ p: 2.5 }}>
        <Skeleton width="70%" height={28} />
        <Skeleton width="40%" height={22} sx={{ mt: 1 }} />
      </Box>
    </Paper>
  </Grid>
);

export default function Wishlist() {
  const navigate = useNavigate();

  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState("");

  const fetchWishlist = useCallback(async () => {
    const storedUserId = sessionStorage.getItem("userId");

    if (!storedUserId) {
      setError("User not logged in");
      setLoading(false);
      navigate("/Signin");
      return;
    }

    setUserId(storedUserId);
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/${config.getUserWishlist}`,
        {
          headers: {
            "user-id": storedUserId,
          },
        },
      );

      const products = response.data?.WishListData?.productData || [];

      setWishlistItems(products);
    } catch (err) {
      setError("Failed to fetch wishlist.");
      setWishlistItems([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const handleRemoveFromWishlist = async (productId) => {
    const storedUserId = sessionStorage.getItem("userId");

    if (!storedUserId) {
      navigate("/Signin");
      return;
    }

    try {
      await axios.post(
        `${config.apiBaseUrl}/${config.removeFromWishlist}`,
        {},
        {
          headers: {
            "user-id": storedUserId,
            "product-id": productId,
          },
        },
      );

      setWishlistItems((previous) =>
        previous.filter((item) => item?.product?.productId !== productId),
      );
    } catch (err) {
      setError("Failed to remove item from wishlist.");
    }
  };

  const openAppRedirect = () => {
    const appPackage = "com.ytctechventures.hindustanwardrobe";
    const intentUrl = `intent://ownerProfile?ownerId=${userId}#Intent;scheme=https;package=${appPackage};end`;

    window.location.href = intentUrl;

    setTimeout(() => {
      window.location.href = `https://play.google.com/store/apps/details?id=${appPackage}`;
    }, 1200);
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          backgroundColor: COLORS.cream,
          minHeight: "100vh",
          py: { xs: 4, md: 7 },
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "250px minmax(0, 1fr)",
              },
              gap: { xs: 0, md: 5 },
              alignItems: "start",
            }}
          >
            <ProfileSideBar active="wishlist" />

            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  mb: { xs: 4, md: 5 },
                  borderBottom: `1px solid ${COLORS.border}`,
                  pb: { xs: 3, md: 4 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: COLORS.gold,
                    mb: 1,
                  }}
                >
                  Your saved collection
                </Typography>

                <Typography
                  component="h1"
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: {
                      xs: 34,
                      sm: 42,
                      md: 52,
                    },
                    fontWeight: 400,
                    lineHeight: 1.08,
                    color: COLORS.black,
                    mb: 1.5,
                  }}
                >
                  Wishlist
                </Typography>

                <Typography
                  sx={{
                    maxWidth: 620,
                    color: COLORS.grey,
                    fontSize: 14,
                    lineHeight: 1.8,
                  }}
                >
                  The pieces you love, saved for whenever you are ready to make
                  them part of your next occasion.
                </Typography>
              </Box>

              {!loading && !error && (
                <Paper
                  elevation={0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.white,
                    borderRadius: 0,
                    px: { xs: 2, md: 3 },
                    py: 2,
                    mb: 3,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <FavoriteBorderIcon
                      sx={{
                        color: COLORS.gold,
                        fontSize: 21,
                      }}
                    />

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 9,
                          textTransform: "uppercase",
                          letterSpacing: "0.14em",
                          color: COLORS.grey,
                        }}
                      >
                        Saved Pieces
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontSize: 23,
                          color: COLORS.black,
                        }}
                      >
                        {wishlistItems.length}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    component="button"
                    onClick={() => navigate("/AllProduct")}
                    sx={{
                      border: 0,
                      background: "transparent",
                      color: COLORS.mutedBlack,
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      cursor: "pointer",
                      transition: "color 0.2s ease",
                      "&:hover": {
                        color: COLORS.gold,
                      },
                    }}
                  >
                    Explore More
                  </Box>
                </Paper>
              )}

              {loading ? (
                <Grid container spacing={2.5}>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <WishlistSkeleton key={index} />
                  ))}
                </Grid>
              ) : error ? (
                <Paper
                  elevation={0}
                  sx={{
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.white,
                    borderRadius: 0,
                    p: { xs: 4, md: 6 },
                    textAlign: "center",
                  }}
                >
                  <FavoriteBorderIcon
                    sx={{
                      fontSize: 46,
                      color: COLORS.gold,
                      mb: 2,
                    }}
                  />

                  <Typography
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: 29,
                      color: COLORS.black,
                      mb: 1,
                    }}
                  >
                    Something went wrong
                  </Typography>

                  <Typography
                    sx={{
                      color: COLORS.grey,
                      fontSize: 14,
                      lineHeight: 1.8,
                      mb: 3,
                    }}
                  >
                    {error}
                  </Typography>

                  <Box
                    component="button"
                    onClick={fetchWishlist}
                    sx={{
                      border: `1px solid ${COLORS.black}`,
                      backgroundColor: COLORS.black,
                      color: COLORS.white,
                      px: 3.5,
                      py: 1.5,
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      cursor: "pointer",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        backgroundColor: COLORS.gold,
                        borderColor: COLORS.gold,
                      },
                    }}
                  >
                    Try Again
                  </Box>
                </Paper>
              ) : wishlistItems.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.white,
                    borderRadius: 0,
                    p: { xs: 4, md: 7 },
                    textAlign: "center",
                  }}
                >
                  <FavoriteBorderIcon
                    sx={{
                      fontSize: 52,
                      color: COLORS.gold,
                      mb: 2,
                    }}
                  />

                  <Typography
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: 31,
                      color: COLORS.black,
                      mb: 1,
                    }}
                  >
                    Your wishlist is empty
                  </Typography>

                  <Typography
                    sx={{
                      maxWidth: 470,
                      mx: "auto",
                      color: COLORS.grey,
                      fontSize: 14,
                      lineHeight: 1.8,
                      mb: 3,
                    }}
                  >
                    Save the styles you love and come back whenever you are
                    ready to rent them for your next occasion.
                  </Typography>

                  <Box
                    component="button"
                    onClick={() => navigate("/AllProduct")}
                    sx={{
                      border: `1px solid ${COLORS.black}`,
                      backgroundColor: COLORS.black,
                      color: COLORS.white,
                      px: 3.5,
                      py: 1.5,
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      cursor: "pointer",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        backgroundColor: COLORS.gold,
                        borderColor: COLORS.gold,
                      },
                    }}
                  >
                    Explore Collection
                  </Box>
                </Paper>
              ) : (
                <Grid container spacing={2.5}>
                  {wishlistItems.map((item) => {
                    const product = item?.product;
                    const productId = product?.productId;
                    const image = item?.productImage?.[0];
                    const title = product?.title || "Wardrobe Item";
                    const rent = product?.rentAmount;

                    return (
                      <Grid item xs={12} sm={6} lg={4} key={productId}>
                        <Card
                          elevation={0}
                          sx={{
                            height: "100%",
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 0,
                            overflow: "hidden",
                            backgroundColor: COLORS.white,
                            position: "relative",
                            cursor: productId ? "pointer" : "default",
                            transition:
                              "transform 0.25s ease, box-shadow 0.25s ease",
                            "&:hover": productId
                              ? {
                                  transform: "translateY(-5px)",
                                  boxShadow: "0 16px 35px rgba(23,23,23,0.11)",
                                }
                              : {},
                            "&:hover .wishlist-image": {
                              transform: "scale(1.035)",
                            },
                            "&:hover .view-label": {
                              opacity: 1,
                              transform: "translateY(0)",
                            },
                          }}
                          onClick={() => {
                            if (productId) {
                              navigate(`/productdetail/${productId}`);
                            }
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              overflow: "hidden",
                              backgroundColor: COLORS.softCream,
                            }}
                          >
                            <CardMedia
                              component="img"
                              className="wishlist-image"
                              image={image || ""}
                              alt={title}
                              sx={{
                                width: "100%",
                                height: {
                                  xs: 360,
                                  sm: 320,
                                },
                                objectFit: "cover",
                                transition: "transform 0.5s ease",
                              }}
                            />

                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                handleRemoveFromWishlist(productId);
                              }}
                              aria-label="Remove from wishlist"
                              sx={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                width: 38,
                                height: 38,
                                borderRadius: 0,
                                backgroundColor: COLORS.white,
                                border: `1px solid ${COLORS.border}`,
                                color: COLORS.black,
                                "&:hover": {
                                  backgroundColor: COLORS.black,
                                  color: COLORS.white,
                                },
                              }}
                            >
                              <WishlistIcon
                                sx={{
                                  fontSize: 19,
                                }}
                              />
                            </IconButton>

                            <Box
                              className="view-label"
                              sx={{
                                position: "absolute",
                                bottom: 14,
                                left: 14,
                                right: 14,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                py: 1.2,
                                backgroundColor: "rgba(23,23,23,0.88)",
                                color: COLORS.white,
                                opacity: 0,
                                transform: "translateY(8px)",
                                transition: "all 0.25s ease",
                              }}
                            >
                              <VisibilityOutlinedIcon sx={{ fontSize: 15 }} />

                              <Typography
                                sx={{
                                  fontSize: 9,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.14em",
                                }}
                              >
                                View Product
                              </Typography>
                            </Box>
                          </Box>

                          <CardContent
                            sx={{
                              p: 2.5,
                              "&:last-child": {
                                pb: 2.5,
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 9,
                                textTransform: "uppercase",
                                letterSpacing: "0.15em",
                                color: COLORS.gold,
                                mb: 0.8,
                              }}
                            >
                              Saved to Wishlist
                            </Typography>

                            <Typography
                              sx={{
                                fontFamily: "Georgia, serif",
                                fontSize: 22,
                                lineHeight: 1.2,
                                color: COLORS.black,
                                textTransform: "capitalize",
                                mb: 1.5,
                              }}
                            >
                              {title}
                            </Typography>

                            <Divider
                              sx={{
                                borderColor: COLORS.border,
                                mb: 1.5,
                              }}
                            />

                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: 9,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.12em",
                                    color: COLORS.grey,
                                    mb: 0.3,
                                  }}
                                >
                                  Rent
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: 17,
                                    fontWeight: 600,
                                    color: COLORS.black,
                                  }}
                                >
                                  ₹{rent ?? 0}
                                </Typography>
                              </Box>

                              <ArrowForwardIosRoundedIcon
                                sx={{
                                  fontSize: 14,
                                  color: COLORS.lightGrey,
                                }}
                              />
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
}
